/**
 * Configurator Settings Page
 * 
 * Unified settings page for configurator module:
 * - General settings (default currency, tax rate, etc.)
 * - Email notification settings (SMTP, default templates)
 * - PDF/Document settings (templates, branding)
 * - Public wizard customization (colors, logo)
 * 
 * MAGE-305
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  PaintBrushIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import configuratorService from '../services/configuratorService';

// ==========================================
// Styled Components
// ==========================================

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f3f4f6;
`;

const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background: #f9fafb;
    color: #374151;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
  p {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0.25rem 0 0 0;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 1rem;
  height: fit-content;
  position: sticky;
  top: 1.5rem;
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? '#eff6ff' : 'transparent'};
  color: ${props => props.active ? '#2563eb' : '#4b5563'};
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '500' : '400'};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#eff6ff' : '#f9fafb'};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: #3b82f6;
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }
  
  .help-text {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    &:disabled {
      background: #f9fafb;
      color: #6b7280;
    }
  }
  
  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.cols || 2}, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ColorInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  input[type="color"] {
    width: 48px;
    height: 36px;
    padding: 2px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
  }
  
  input[type="text"] {
    flex: 1;
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  
  input {
    width: auto;
    position: relative;
    width: 44px;
    height: 24px;
    appearance: none;
    background: #d1d5db;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s;
    
    &:checked {
      background: #3b82f6;
    }
    
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
    
    &:checked::before {
      transform: translateX(20px);
    }
  }
  
  span {
    font-size: 0.875rem;
    color: #374151;
  }
`;

const Button = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &.primary {
    background: #3b82f6;
    color: white;
    border: none;
    
    &:hover:not(:disabled) { background: #2563eb; }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover:not(:disabled) { background: #f9fafb; }
  }
  
  &.success {
    background: #10b981;
    color: white;
    border: none;
    
    &:hover:not(:disabled) { background: #059669; }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  &.success {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }
  
  &.error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }
  
  &.info {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const TestResultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.success {
    background: #d1fae5;
    color: #065f46;
  }
  
  &.error {
    background: #fee2e2;
    color: #991b1b;
  }
`;

// ==========================================
// Component
// ==========================================

function ConfiguratorSettings() {
  const navigate = useNavigate();
  
  const [activeSection, setActiveSection] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    // General
    default_currency: 'EUR',
    default_tax_rate: 22,
    show_prices_with_tax: true,
    require_customer_info: true,
    auto_generate_order_number: true,
    order_number_prefix: 'QUO-',
    
    // Email
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    send_customer_confirmation: true,
    send_admin_notification: true,
    admin_notification_emails: '',
    
    // PDF/Documents
    pdf_template: 'quote',
    pdf_logo_url: '',
    pdf_company_name: '',
    pdf_company_address: '',
    pdf_company_vat: '',
    pdf_footer_text: '',
    pdf_show_bank_details: false,
    pdf_bank_name: '',
    pdf_bank_iban: '',
    pdf_bank_swift: '',
    
    // Wizard Customization
    wizard_primary_color: '#3b82f6',
    wizard_secondary_color: '#10b981',
    wizard_logo_url: '',
    wizard_header_text: '',
    wizard_footer_text: '',
    wizard_show_progress_bar: true,
    wizard_show_price_summary: true,
    wizard_thank_you_message: 'Thank you for your submission! We will contact you shortly.',
    wizard_redirect_url: '',
  });

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/configurators/settings');
      if (response.data.settings) {
        setSettings(prev => ({ ...prev, ...response.data.settings }));
      }
    } catch (err) {
      // Settings might not exist yet, use defaults
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save settings
  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      await api.put('/configurators/settings', { settings });
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (err) {
      console.error('Failed to save settings:', err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  // Test email connection
  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      setEmailTestResult(null);
      
      const response = await api.post('/configurators/settings/test-email', {
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_username: settings.smtp_username,
        smtp_password: settings.smtp_password,
        smtp_from_email: settings.smtp_from_email,
        smtp_use_tls: settings.smtp_use_tls,
      });
      
      setEmailTestResult({ success: true, message: response.data.message || 'Connection successful!' });
    } catch (err) {
      setEmailTestResult({ success: false, message: err.response?.data?.error || 'Connection failed' });
    } finally {
      setTestingEmail(false);
    }
  };

  // Seed default statuses
  const handleSeedStatuses = async () => {
    try {
      await configuratorService.seedDefaultStatuses();
      setMessage({ type: 'success', text: 'Default pipeline statuses created!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create statuses' });
    }
  };

  // Seed email templates
  const handleSeedTemplates = async () => {
    try {
      await configuratorService.seedEmailTemplates();
      setMessage({ type: 'success', text: 'Default email templates created!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create templates' });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          Loading settings...
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <BackButton onClick={() => navigate('/configurators')}>
            <ArrowLeftIcon />
            Back
          </BackButton>
          <HeaderTitle>
            <h1>⚙️ Configurator Settings</h1>
            <p>Configure defaults for your product configurators</p>
          </HeaderTitle>
        </HeaderContent>
      </Header>
      
      <Content>
        {/* Sidebar Navigation */}
        <Sidebar>
          <NavItem
            active={activeSection === 'general'}
            onClick={() => setActiveSection('general')}
          >
            <Cog6ToothIcon />
            General
          </NavItem>
          <NavItem
            active={activeSection === 'email'}
            onClick={() => setActiveSection('email')}
          >
            <EnvelopeIcon />
            Email Settings
          </NavItem>
          <NavItem
            active={activeSection === 'documents'}
            onClick={() => setActiveSection('documents')}
          >
            <DocumentTextIcon />
            PDF & Documents
          </NavItem>
          <NavItem
            active={activeSection === 'wizard'}
            onClick={() => setActiveSection('wizard')}
          >
            <PaintBrushIcon />
            Wizard Appearance
          </NavItem>
        </Sidebar>
        
        {/* Main Content */}
        <MainContent>
          {message && (
            <Alert className={message.type}>
              {message.type === 'success' ? <CheckCircleIcon /> : <ExclamationCircleIcon />}
              <span>{message.text}</span>
            </Alert>
          )}
          
          {/* General Settings */}
          {activeSection === 'general' && (
            <Card>
              <CardHeader>
                <Cog6ToothIcon />
                <h2>General Settings</h2>
              </CardHeader>
              <CardBody>
                <FormRow>
                  <FormGroup>
                    <label>Default Currency</label>
                    <select
                      name="default_currency"
                      value={settings.default_currency}
                      onChange={handleChange}
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="CHF">CHF</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Default Tax Rate (%)</label>
                    <input
                      type="number"
                      name="default_tax_rate"
                      value={settings.default_tax_rate}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <label>Order Number Prefix</label>
                    <input
                      type="text"
                      name="order_number_prefix"
                      value={settings.order_number_prefix}
                      onChange={handleChange}
                      placeholder="e.g., QUO-"
                    />
                    <span className="help-text">Prefix for auto-generated order numbers</span>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>&nbsp;</label>
                    <Toggle>
                      <input
                        type="checkbox"
                        name="auto_generate_order_number"
                        checked={settings.auto_generate_order_number}
                        onChange={handleChange}
                      />
                      <span>Auto-generate order numbers</span>
                    </Toggle>
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <Toggle>
                    <input
                      type="checkbox"
                      name="show_prices_with_tax"
                      checked={settings.show_prices_with_tax}
                      onChange={handleChange}
                    />
                    <span>Show prices with tax included</span>
                  </Toggle>
                </FormGroup>
                
                <FormGroup>
                  <Toggle>
                    <input
                      type="checkbox"
                      name="require_customer_info"
                      checked={settings.require_customer_info}
                      onChange={handleChange}
                    />
                    <span>Require customer information before submission</span>
                  </Toggle>
                </FormGroup>
                
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Quick Actions</h3>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <Button className="secondary" onClick={handleSeedStatuses}>
                      🎯 Create Default Pipeline Statuses
                    </Button>
                    <Button className="secondary" onClick={handleSeedTemplates}>
                      ✉️ Create Default Email Templates
                    </Button>
                    <Button className="secondary" onClick={() => navigate('/configurators/pipeline-settings')}>
                      ⚙️ Manage Pipeline Statuses
                    </Button>
                    <Button className="secondary" onClick={() => navigate('/configurators/email-templates')}>
                      📧 Manage Email Templates
                    </Button>
                  </div>
                </div>
              </CardBody>
              <CardFooter>
                <Button className="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Settings'}
                </Button>
              </CardFooter>
            </Card>
          )}
          
          {/* Email Settings */}
          {activeSection === 'email' && (
            <Card>
              <CardHeader>
                <EnvelopeIcon />
                <h2>Email Settings</h2>
              </CardHeader>
              <CardBody>
                <Alert className="info">
                  Configure SMTP settings to send email notifications. Leave empty to use system defaults.
                </Alert>
                
                <FormRow>
                  <FormGroup>
                    <label>SMTP Host</label>
                    <input
                      type="text"
                      name="smtp_host"
                      value={settings.smtp_host}
                      onChange={handleChange}
                      placeholder="e.g., smtp.gmail.com"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <label>SMTP Port</label>
                    <input
                      type="number"
                      name="smtp_port"
                      value={settings.smtp_port}
                      onChange={handleChange}
                      placeholder="587"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <label>SMTP Username</label>
                    <input
                      type="text"
                      name="smtp_username"
                      value={settings.smtp_username}
                      onChange={handleChange}
                      placeholder="username@domain.com"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <label>SMTP Password</label>
                    <input
                      type="password"
                      name="smtp_password"
                      value={settings.smtp_password}
                      onChange={handleChange}
                      placeholder="••••••••"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <label>From Email</label>
                    <input
                      type="email"
                      name="smtp_from_email"
                      value={settings.smtp_from_email}
                      onChange={handleChange}
                      placeholder="noreply@yourcompany.com"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <label>From Name</label>
                    <input
                      type="text"
                      name="smtp_from_name"
                      value={settings.smtp_from_name}
                      onChange={handleChange}
                      placeholder="Your Company Name"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <Toggle>
                    <input
                      type="checkbox"
                      name="smtp_use_tls"
                      checked={settings.smtp_use_tls}
                      onChange={handleChange}
                    />
                    <span>Use TLS encryption</span>
                  </Toggle>
                </FormGroup>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                  <Button
                    className="secondary"
                    onClick={handleTestEmail}
                    disabled={testingEmail || !settings.smtp_host}
                  >
                    {testingEmail ? 'Testing...' : '🔌 Test Connection'}
                  </Button>
                  {emailTestResult && (
                    <TestResultBadge className={emailTestResult.success ? 'success' : 'error'}>
                      {emailTestResult.success ? '✓' : '✗'} {emailTestResult.message}
                    </TestResultBadge>
                  )}
                </div>
                
                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Notification Preferences</h3>
                  
                  <FormGroup>
                    <Toggle>
                      <input
                        type="checkbox"
                        name="send_customer_confirmation"
                        checked={settings.send_customer_confirmation}
                        onChange={handleChange}
                      />
                      <span>Send confirmation email to customer after submission</span>
                    </Toggle>
                  </FormGroup>
                  
                  <FormGroup>
                    <Toggle>
                      <input
                        type="checkbox"
                        name="send_admin_notification"
                        checked={settings.send_admin_notification}
                        onChange={handleChange}
                      />
                      <span>Send notification to admin(s) for new submissions</span>
                    </Toggle>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Admin Notification Emails</label>
                    <input
                      type="text"
                      name="admin_notification_emails"
                      value={settings.admin_notification_emails}
                      onChange={handleChange}
                      placeholder="admin@company.com, sales@company.com"
                    />
                    <span className="help-text">Comma-separated list of email addresses</span>
                  </FormGroup>
                </div>
              </CardBody>
              <CardFooter>
                <Button className="secondary" onClick={() => navigate('/configurators/email-templates')}>
                  📧 Manage Templates
                </Button>
                <Button className="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Settings'}
                </Button>
              </CardFooter>
            </Card>
          )}
          
          {/* PDF & Documents Settings */}
          {activeSection === 'documents' && (
            <Card>
              <CardHeader>
                <DocumentTextIcon />
                <h2>PDF & Document Settings</h2>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <label>Default PDF Template</label>
                  <select
                    name="pdf_template"
                    value={settings.pdf_template}
                    onChange={handleChange}
                  >
                    <option value="quote">Quote Template</option>
                    <option value="invoice">Invoice Template</option>
                    <option value="simple">Simple Template</option>
                  </select>
                </FormGroup>
                
                <FormGroup>
                  <label>Company Logo URL</label>
                  <input
                    type="url"
                    name="pdf_logo_url"
                    value={settings.pdf_logo_url}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com/logo.png"
                  />
                  <span className="help-text">Logo will appear on PDF documents</span>
                </FormGroup>
                
                <FormRow>
                  <FormGroup>
                    <label>Company Name</label>
                    <input
                      type="text"
                      name="pdf_company_name"
                      value={settings.pdf_company_name}
                      onChange={handleChange}
                      placeholder="Your Company Ltd."
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <label>VAT Number</label>
                    <input
                      type="text"
                      name="pdf_company_vat"
                      value={settings.pdf_company_vat}
                      onChange={handleChange}
                      placeholder="SI12345678"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label>Company Address</label>
                  <textarea
                    name="pdf_company_address"
                    value={settings.pdf_company_address}
                    onChange={handleChange}
                    placeholder="Street Address&#10;City, Postal Code&#10;Country"
                    rows={3}
                  />
                </FormGroup>
                
                <FormGroup>
                  <label>PDF Footer Text</label>
                  <input
                    type="text"
                    name="pdf_footer_text"
                    value={settings.pdf_footer_text}
                    onChange={handleChange}
                    placeholder="Thank you for your business!"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Toggle>
                    <input
                      type="checkbox"
                      name="pdf_show_bank_details"
                      checked={settings.pdf_show_bank_details}
                      onChange={handleChange}
                    />
                    <span>Show bank details on PDF</span>
                  </Toggle>
                </FormGroup>
                
                {settings.pdf_show_bank_details && (
                  <FormRow cols={3}>
                    <FormGroup>
                      <label>Bank Name</label>
                      <input
                        type="text"
                        name="pdf_bank_name"
                        value={settings.pdf_bank_name}
                        onChange={handleChange}
                        placeholder="Bank Name"
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <label>IBAN</label>
                      <input
                        type="text"
                        name="pdf_bank_iban"
                        value={settings.pdf_bank_iban}
                        onChange={handleChange}
                        placeholder="SI56 1234 5678 9012 3456"
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <label>SWIFT/BIC</label>
                      <input
                        type="text"
                        name="pdf_bank_swift"
                        value={settings.pdf_bank_swift}
                        onChange={handleChange}
                        placeholder="BANKSIXX"
                      />
                    </FormGroup>
                  </FormRow>
                )}
              </CardBody>
              <CardFooter>
                <Button className="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Settings'}
                </Button>
              </CardFooter>
            </Card>
          )}
          
          {/* Wizard Appearance */}
          {activeSection === 'wizard' && (
            <Card>
              <CardHeader>
                <PaintBrushIcon />
                <h2>Wizard Appearance</h2>
              </CardHeader>
              <CardBody>
                <Alert className="info">
                  Customize the look and feel of your public configurator wizard. These settings apply as defaults for all configurators.
                </Alert>
                
                <FormRow>
                  <FormGroup>
                    <label>Primary Color</label>
                    <ColorInput>
                      <input
                        type="color"
                        name="wizard_primary_color"
                        value={settings.wizard_primary_color}
                        onChange={handleChange}
                      />
                      <input
                        type="text"
                        name="wizard_primary_color"
                        value={settings.wizard_primary_color}
                        onChange={handleChange}
                      />
                    </ColorInput>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Secondary Color</label>
                    <ColorInput>
                      <input
                        type="color"
                        name="wizard_secondary_color"
                        value={settings.wizard_secondary_color}
                        onChange={handleChange}
                      />
                      <input
                        type="text"
                        name="wizard_secondary_color"
                        value={settings.wizard_secondary_color}
                        onChange={handleChange}
                      />
                    </ColorInput>
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label>Logo URL</label>
                  <input
                    type="url"
                    name="wizard_logo_url"
                    value={settings.wizard_logo_url}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com/logo.png"
                  />
                </FormGroup>
                
                <FormRow>
                  <FormGroup>
                    <label>Header Text</label>
                    <input
                      type="text"
                      name="wizard_header_text"
                      value={settings.wizard_header_text}
                      onChange={handleChange}
                      placeholder="Configure Your Product"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Footer Text</label>
                    <input
                      type="text"
                      name="wizard_footer_text"
                      value={settings.wizard_footer_text}
                      onChange={handleChange}
                      placeholder="© 2024 Your Company"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <Toggle>
                    <input
                      type="checkbox"
                      name="wizard_show_progress_bar"
                      checked={settings.wizard_show_progress_bar}
                      onChange={handleChange}
                    />
                    <span>Show progress bar</span>
                  </Toggle>
                </FormGroup>
                
                <FormGroup>
                  <Toggle>
                    <input
                      type="checkbox"
                      name="wizard_show_price_summary"
                      checked={settings.wizard_show_price_summary}
                      onChange={handleChange}
                    />
                    <span>Show running price summary</span>
                  </Toggle>
                </FormGroup>
                
                <FormGroup>
                  <label>Thank You Message</label>
                  <textarea
                    name="wizard_thank_you_message"
                    value={settings.wizard_thank_you_message}
                    onChange={handleChange}
                    placeholder="Thank you for your submission!"
                    rows={3}
                  />
                </FormGroup>
                
                <FormGroup>
                  <label>Redirect URL (after submission)</label>
                  <input
                    type="url"
                    name="wizard_redirect_url"
                    value={settings.wizard_redirect_url}
                    onChange={handleChange}
                    placeholder="https://yoursite.com/thank-you (optional)"
                  />
                  <span className="help-text">Leave empty to show thank you message instead</span>
                </FormGroup>
              </CardBody>
              <CardFooter>
                <Button className="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Settings'}
                </Button>
              </CardFooter>
            </Card>
          )}
        </MainContent>
      </Content>
    </PageContainer>
  );
}

export default ConfiguratorSettings;
